import { useState, useEffect } from 'react';
import { useError } from '../../context/ErrorContext';
import { headerService } from '../../services/headerService';
import { Save, X, Plus, Trash2 } from 'lucide-react';

const HeaderContentManager = ({ setEditingHeader, updatePageLastUpdated }) => {
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const { showError } = useError();

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      try {
        const content = await headerService.getHeaderContent();
        setPageContent(content);
      } catch (error) {
        showError('Failed to load Header content: ' + error.message, 'error');
        setPageContent(null);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, [showError]);

  const handleSaveContent = async () => {
    try {
      setLoading(true);
      const updatedContent = await headerService.createOrUpdateHeaderContent(pageContent);
      setPageContent(updatedContent);
      updatePageLastUpdated('Header', updatedContent.lastUpdated);
      showError('Header updated successfully!', 'success');
    } catch (error) {
      showError('Failed to save Header content: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setPageContent(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (arrayName, index, field, value) => {
    const updatedArray = [...pageContent[arrayName]];
    updatedArray[index] = { ...updatedArray[index], [field]: value };
    setPageContent(prev => ({
      ...prev,
      [arrayName]: updatedArray,
    }));
  };

  const handleAddToArray = (arrayName, newItem) => {
    setPageContent(prev => ({
      ...prev,
      [arrayName]: [...prev[arrayName], newItem],
    }));
  };

  const handleRemoveFromArray = (arrayName, index) => {
    setPageContent(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index),
    }));
  };

  if (!pageContent) {
    return (
      <div className="flex items-center justify-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-xl">
        <div className="flex flex-col items-center">
          <svg className="animate-spin h-8 w-8 text-accent dark:text-accent mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-lg text-gray-700 dark:text-gray-300">Loading Header editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button
          onClick={() => setEditingHeader(false)}
          className="flex items-center px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg shadow-md hover:bg-gray-300 dark:hover:bg-gray-600 transition duration-200 font-medium"
        >
          <X className="w-4 h-4 mr-2" />
          Close
        </button>
      </div>

      {/* Nav Links */}
      <div className="p-5 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Header Navigation Links</h3>
        <div className="space-y-4">
          {pageContent.navItems.map((item, index) => (
            <div key={index} className="flex items-center space-x-4 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
              <div className="flex-grow space-y-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                  <input
                    type="text"
                    value={item.name}
                    onChange={(e) => handleArrayChange('navItems', index, 'name', e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Path</label>
                  <input
                    type="text"
                    value={item.path}
                    onChange={(e) => handleArrayChange('navItems', index, 'path', e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm p-2.5 text-gray-800 dark:text-gray-200 focus:ring-accent focus:border-accent dark:bg-gray-900 transition duration-200"
                  />
                </div>
              </div>
              <button
                onClick={() => handleRemoveFromArray('navItems', index)}
                className="p-2 text-red-500 hover:text-red-700 transition duration-200"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}
          <button
            onClick={() => handleAddToArray('navItems', { name: '', path: '' })}
            className="flex items-center px-4 py-2 bg-green-300 dark:bg-green-400 text-gray-800 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition duration-200 font-medium"
          >
            <Plus size={20} className="mr-2" /> Add New Link
          </button>
        </div>
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSaveContent}
            className="flex items-center px-6 py-2.5 bg-accent dark:bg-accent text-white rounded-lg shadow-md hover:bg-accent/80 dark:hover:bg-accent/80 transition duration-200 font-semibold"
            disabled={loading}
          >
            <Save className="w-4 h-4 mr-2" /> Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default HeaderContentManager;